<template>
	<div class="container new-member-experiences pb-8">
		<div class="row justify-content-center">
			<div v-if="loading" class="col-md-10 col-lg-8">
				<div class="bg-white rounded-3 bg-opacity-75 overflow-hidden">
					<div class="d-flex align-items-center justify-content-center my-8 py-8">
						<div class="spinner-border" role="status">
							<span class="visually-hidden">Loading...</span>
						</div>
					</div>
				</div>
			</div>
			<div v-if="!loading" class="col-md-10 col-lg-8">
				<div class="bg-white rounded-3 bg-opacity-75 overflow-hidden">
					<new-member-experience-item
						v-for="(item, index) in experiences"
						:key="item.title"
						v-model="experiences[index]"
						:is-last="index === experiences.length - 1"
					/>
				</div>
				<div class="py-3 d-grid gap-3 d-sm-flex align-items-baseline justify-content-sm-between">
					<button
						class="btn btn-dark order-sm-last"
						data-testid="btn-submit"
						:disabled="disabled"
						@click="submit(false)"
					>
						Continue
					</button>
					<button
						class="btn-reset text-center opacity-75 text-decoration-none"
						data-testid="btn-skip"
						@click="submit(true)"
					>
						Skip this step
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import Cookies from 'js-cookie';
import { computed, onMounted, ref, type Ref } from 'vue';
import { getGlobalPageDriverUrl } from '@/util/globalPageDriver';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import NewMemberExperienceItem from 'Lib/components/new-member-experience/NewMemberExperienceItem.vue';
import { efcConstants, profileService } from '@life-time-inc/enterprise-framework-core';
import { aemServletService } from '@/services/aemServletService';
import { experienceService } from '@/services/experienceService';
import { memberConfig } from '@/util/configs';
import { logError, logWarn } from '@/util/logger';
import type { AemuiExperience } from 'Types/aemui/content-fragments/contentFragment';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';
import type { AemuiExperiencePutRequest } from 'Types/aemui/experiences/experiences';

const loading = ref(true);
const mmsClubId = computed(() => {
	const url = new URL(window.location.href);
	const mmsClubIdString = url.searchParams.get('mmsClubId');
	return mmsClubIdString ? parseInt(mmsClubIdString) : 0;
});
const clubProductPagePath = computed(() => {
	const url = new URL(window.location.href);
	return url.searchParams.get('clubProductPagePath') ?? '';
});
const ssoId = computed(() => {
	const url = new URL(window.location.href);
	const sso = url.searchParams.get('sso') ?? Cookies.get(efcConstants.CookieConstant.ssoId);
	return sso ?? '';
});
const profile: Ref<ltFramework.IMemberDetails> = ref({} as ltFramework.IMemberDetails);
const experiences: Ref<AemuiExperience[]> = ref([]);

const rojp = computed(() =>
	clubProductPagePath.value === 'omsReserveWaitlist' ? 'false' : 'true'
);

const goToConfirmation = () => {
	// Store SSO before clearing cookie because it'll be gone afterwards.
	const sso = ssoId.value;

	// Clear SSO cookie on Prospect, otherwise if the user goes back into OMS the mobile rejoin flow will be triggered.
	Cookies.remove(efcConstants.CookieConstant.ssoId);

	window.location.assign(
		`${memberConfig.url}/clubs.go/${mmsClubId.value}/${clubProductPagePath.value}/lta?sso=${sso}&rojp=${rojp.value}`
	);
};

const getProfile = async () => {
	try {
		const response = await profileService.getProfileData();
		if (!response.memberDetails) {
			throw new Error('No member details');
		}
		profile.value = response.memberDetails;
	} catch (error: unknown) {
		logError('GET profile failed', { ssoId: ssoId.value }, error);
		goToConfirmation();
	}
};

onMounted(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	if (!clubProductPagePath.value || !mmsClubId.value || !ssoId.value) {
		logWarn('Missing URL param', {
			clubProductPagePath: clubProductPagePath.value,
			mmsClubId: mmsClubId.value,
			ssoId: ssoId.value
		});
		const mapUrl = getGlobalPageDriverUrl('locateAClub');
		window.location.assign(mapUrl);
		return;
	}

	await getProfile();

	window.adobeDataLayer.push({
		event: 'OMS Transaction Success',
		memberID: profile.value.memberId ?? ''
	});

	const url = new URL(window.location.href);
	url.searchParams.delete('sso');
	window.history.replaceState(null, '', url);

	try {
		const response = await aemServletService.getExperiences(mmsClubId.value);
		experiences.value = response.data.variations.map((experience) => ({
			...experience,
			selected: false
		}));
	} catch (error: unknown) {
		logError('GET experiences failed', { mmsClubId: mmsClubId.value }, error);
		goToConfirmation();
	} finally {
		if (experiences.value.length === 0) {
			logWarn('No experiences', { mmsClubId: mmsClubId.value });
			goToConfirmation();
		}
		loading.value = false;
	}
});

const disabled = computed(() => {
	return experiences.value.every((experience) => !experience.selected);
});

const submit = async (skip: boolean) => {
	loading.value = true;
	const variations =
		skip === true
			? experiences.value.map((experience) => ({
					...experience,
					selected: false
				}))
			: experiences.value;
	const request: AemuiExperiencePutRequest = {
		memberId: profile.value.memberId,
		clubId: mmsClubId.value,
		confirmationPageDriver: clubProductPagePath.value,
		membershipLevel: profile.value.membershipLevel,
		skip,
		variations
	};
	try {
		await experienceService.put(request);
	} catch (error: unknown) {
		logError('Experience PUT failed', { request }, error);
	} finally {
		goToConfirmation();
	}
};

defineExpose({
	clubProductPagePath,
	experiences,
	loading,
	mmsClubId,
	profile,
	ssoId
});
</script>

<style lang="scss">
.btn-reset {
	background-color: transparent;
	border: 0;
}
</style>
